import { CommonModule, TitleCasePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, Signal, WritableSignal, inject, signal } from '@angular/core';
import { AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { Address, AddressValidationResponse, AddressValidationData, AddressValidationStatus, Company, Construction, Country, CountryStates, IBatSku, IChoice, IMode, MessageType, State, WarrantyIMode, WarrantyImageType, WarrantyImageTypes, OTHER_PLACE_OF_PURCHASE, ClaimReason, ClaimReasons, WarrantyDamageImageTypes } from '@models';
import { todaysDate, normalizeAddressValue, normalizeCountry, normalizeStateForCountry, minPurchaseDate, resolveWarrantyImageType } from '@utilities';
import { ApplicationService, DataService, getErrors, compressImage, MAX_IMAGE_UPLOAD_BYTES, warrantyForm as sharedWarrantyForm } from '@warranty/services';
import { finalize, from, take } from 'rxjs';
import { PurchasedSiteComponent } from '../purchasedSite/purchasedSite.component';
import { SerialConfirmationDialogComponent } from './serial-confirmation-dialog.component';

type WarrantyForm = typeof sharedWarrantyForm;
type WarrantyFormPage = 'personal' | 'warranty';
type AddressValidationCardClass = 'address-validation-fix' | 'address-validation-confirm' | 'address-validation-accept';

export interface WarrantyImageLoaded {
  imageType: WarrantyImageType;
  file: File;
  value: string;
}

const missingReceiptImageError = 'A receipt or proof of purchase image is required.';
// Claims only:
const missingDamageImageError = 'Please upload at least 1 damage image for this claim.';
const imageTooLargeError = 'File is too large, please use a smaller image';

@Component({
  selector: 'app-warranty-form',
  imports: [CommonModule, ReactiveFormsModule, MatInputModule, MatSelectModule, MatButtonModule, MatIcon, RouterModule, MatCardModule, MatTooltipModule, MatDatepickerModule, MatDialogModule, MatProgressBarModule, MatNativeDateModule, MatRadioModule, MatAutocompleteModule, PurchasedSiteComponent, TitleCasePipe],
  templateUrl: './warranty-form.component.html',
  styleUrl: './warranty-form.component.scss',
  providers: [
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline', floatLabel: 'always', subscriptSizing: 'dynamic' } },
  ],
})
export class WarrantyFormComponent implements OnInit {
  readonly applicationService = inject(ApplicationService);
  private readonly dataService = inject(DataService);
  private readonly dialog = inject(MatDialog);

  @Input() warrantyForm: WarrantyForm = sharedWarrantyForm;
  @Input() mode: WarrantyIMode = IMode.Registration;
  @Input() editable = true;
  @Input() canCreateClaim = false;
  @Input() processing = false;
  @Input() purchasedSite!: WritableSignal<string>;
  @Input() company!: Signal<Company>;
  @Input() companySite!: Signal<{ logo: string }>;
  @Input() constructionType!: Signal<Construction>;
  @Input() levelsOfPlay!: Signal<IChoice[]>;
  @Input() places!: Signal<IChoice[]>;
  @Input() filteredModels!: Signal<{ model: string, description: string }[] | undefined>;
  @Input() selectedSku!: Signal<IBatSku | undefined>;
  @Input() colors!: Signal<string[] | undefined>;
  @Input() sizes!: Signal<string[] | undefined>;

  @Output() submitWarranty = new EventEmitter<void>();
  @Output() createClaim = new EventEmitter<void>();
  @Output() modelChanged = new EventEmitter<Event>();
  @Output() modelSelected = new EventEmitter<MatAutocompleteSelectedEvent>();
  @Output() batLengthChanged = new EventEmitter<MatSelectChange>();
  @Output() batColorChanged = new EventEmitter<MatSelectChange>();
  @Output() countryChanged = new EventEmitter<MatSelectChange>();
  @Output() imageLoaded = new EventEmitter<WarrantyImageLoaded>();

  Company = Company;
  Construction = Construction;
  IMode = IMode;
  Country = Country;
  countries = Object.values(Country);
  countryStates = CountryStates;
  todaysDate = todaysDate;
  minPurchaseDate = minPurchaseDate;
  getErrors = getErrors;
  OTHER_PLACE_OF_PURCHASE = OTHER_PLACE_OF_PURCHASE
  currentPage = signal<WarrantyFormPage>('personal');
  addressValidationResponse = signal<AddressValidationResponse | undefined>(undefined);
  addressValidationError = signal<string | undefined>(undefined);
  imageValidationError = signal<string | undefined>(undefined);
  isAddressValidationPending = signal(false);
  private lastValidClaimAddress: Address | undefined;

  ngOnInit() {
    if (this.mode !== IMode.Claim || !this.editable || this.warrantyForm.controls.reason.value) return;

    const reasons = this.claimReasons();
    if (reasons.length === 1) {
      this.warrantyForm.controls.reason.setValue(reasons[0]);
    }
  }

  claimReasons(): ClaimReason[] {
    const isWood = this.constructionType() === Construction.WOOD;
    return ClaimReasons.filter(option => option.isWood === isWood).map(option => option.reason);
  }

  getStates(): readonly State[] {
    return this.countryStates[normalizeCountry(this.warrantyForm.controls.country.value)];
  }

  onContinue(): void {
    if (!this.canContinue()) {
      return;
    }

    const controls = this.getPersonalControls();
    this.markControlsForValidation(controls);
    if (controls.some(control => control.invalid)) {
      return;
    }

    if (this.mode === IMode.Claim) {
      this.validateClaimAddress();
      return;
    }

    this.currentPage.set('warranty');
  }

  backToPersonalInfo(): void {
    this.currentPage.set('personal');
  }

  onSubmit(): void {
    if (this.currentPage() !== 'warranty') {
      this.onContinue();
      return;
    }

    const imageValidationError = this.getImageValidationError();
    this.imageValidationError.set(imageValidationError);
    if (imageValidationError) {
      return;
    }

    if (this.shouldConfirmMissingSerial()) {
      this.openSerialConfirmationDialog();
      return;
    }

    this.submitWarranty.emit();
  }

  addressValidationCardClass(status: AddressValidationStatus | undefined): AddressValidationCardClass {
    if (status === AddressValidationStatus.INVALID) {
      return 'address-validation-fix';
    }
    if (status === AddressValidationStatus.VALID) {
      return 'address-validation-accept';
    }
    return 'address-validation-confirm';
  }

  addressValidationTitle(status: AddressValidationStatus | undefined): string {
    if (status === AddressValidationStatus.INVALID) {
      return 'Address needs changes';
    }
    if (status === AddressValidationStatus.VALID) {
      return 'Address accepted';
    }
    return 'Confirm address';
  }

  formatAddressCandidate(candidate: Address): string {
    return [
      candidate.line1,
      candidate.line2,
      `${candidate.city}, ${candidate.state} ${candidate.zipCode}`,
      candidate.country,
    ].filter(value => !!value).join(' ');
  }

  applyAddressCandidate(rawCandidate: Address, status: AddressValidationStatus | undefined): void {
    const controls = this.warrantyForm.controls;
    const country: Country = normalizeCountry(rawCandidate?.country);
    const state: State | '' = normalizeStateForCountry(country, rawCandidate?.state);

    if (!state) {
      throw new Error(`Candidate state is invalid for country. State: ${rawCandidate?.state} | Country: ${rawCandidate?.country}`);
    }

    const address: Address = {
      line1: rawCandidate?.line1 ?? '',
      line2: rawCandidate?.line2 ?? '',
      city: rawCandidate?.city ?? '',
      state: state,
      zipCode: rawCandidate?.zipCode ?? '',
      country: country
    };

    controls.line1.setValue(address.line1);
    controls.line2.setValue(address.line2);
    controls.city.setValue(address.city);
    controls.state.setValue(state);
    controls.zipCode.setValue(address.zipCode);
    controls.country.setValue(country);
    this.resetAddressControlState();
    this.lastValidClaimAddress = (status === AddressValidationStatus.VALID || status === AddressValidationStatus.CONFIRM) ? address : undefined;
    this.clearAddressValidationResult();
  }

  canContinue(): boolean {
    return !this.processing && !this.isAddressValidationPending();
  }

  getImageTypes(): WarrantyImageType[] {
    let result: WarrantyImageType[] = [];
    const showSerialFields = this.shouldShowSerialFields();
    if (this.mode === IMode.Registration) {
      result = showSerialFields ? ['receipt', 'serial'] : ['receipt'];
    } else {
      result = showSerialFields ? [...WarrantyImageTypes] : WarrantyImageTypes.filter(x => x !== 'serial');
    }
    return result;
  }

  imageMessage(): string {
    let message = 'Please upload a copy of your receipt';

    if (this.shouldShowSerialFields()) {
      message += ' and a photo of the product serial number';
    }
    if (this.mode === IMode.Claim) {
      message += ' along with up to 3 photos of the product and the reported damage';
    }
    return message + '.';
  }

  shouldShowSerialFields(): boolean {
    return this.constructionType() !== Construction.WOOD;
  }

  isImageRequired(imageType: WarrantyImageType): boolean {
    if (imageType === 'receipt') {
      return true;
    }
    if (imageType === 'serial') {
      return false;
    }
    return imageType === 'damage1' && this.mode === IMode.Claim;
  }

  hasUploadedImages(): boolean {
    return this.getImageTypes().some(imageType => !!this.warrantyForm.get(imageType)?.value);
  }

  onDrop(event: DragEvent, imageType: WarrantyImageType): void {
    event.preventDefault();
    if (!event.dataTransfer?.files?.length) {
      this.applicationService.addMessage('No files dropped', MessageType.INFO);
      return;
    }
    this.loadFile(event.dataTransfer.files[0], imageType);
  }

  onFileSelected(event: Event, imageType: WarrantyImageType): void {
    event.preventDefault();
    const element = event.currentTarget as HTMLInputElement;
    const fileList: FileList | null = element.files;
    if (!fileList || fileList.length < 1) {
      return;
    }
    this.loadFile(fileList[0], imageType);
  }

  private getPersonalControls(): AbstractControl[] {
    const controls = this.warrantyForm.controls;
    const personalControls: AbstractControl[] = [
      controls.firstname,
      controls.lastname,
      controls.email,
      controls.phone,
    ];

    if (this.mode === IMode.Claim) {
      personalControls.push(
        controls.line1,
        controls.city,
        controls.state,
        controls.zipCode,
        controls.country,
      );
    }

    return personalControls;
  }

  private markControlsForValidation(controls: AbstractControl[]): void {
    controls.forEach(control => {
      control.markAsDirty();
      control.markAsTouched();
      control.updateValueAndValidity();
    });
  }

  private validateClaimAddress(): void {
    const address: Address = this.getAddressFromForm();
    // If the address is identical to the last valid address from the API, let them through so users don't get stuck constantly validating
    if (this.isLastValidClaimAddress(address)) {
      this.clearAddressValidationResult();
      this.currentPage.set('warranty');
      return;
    }

    this.clearAddressValidationResult();
    this.isAddressValidationPending.set(true);
    this.dataService.validateAddress$(address).pipe(
      finalize(() => {
        this.isAddressValidationPending.set(false);
      })
    ).subscribe({
      next: response => {
        this.handleAddressValidationResponse(response);
      },
      error: (error) => {
        console.error('Address validation failed', error);
        this.lastValidClaimAddress = undefined;
        this.addressValidationError.set('Address validation is unavailable. Please try again.');
      },
    });
  }

  private getAddressFromForm(): Address {
    const controls = this.warrantyForm.controls;
    return {
      line1: controls.line1.value ?? '',
      line2: controls.line2.value ?? '',
      city: controls.city.value ?? '',
      state: controls.state.value ?? '',
      zipCode: controls.zipCode.value ?? '',
      country: controls.country.value ?? '',
    } satisfies Address;
  }

  private handleAddressValidationResponse(response: AddressValidationResponse): void {
    const data = response.data;
    if (response.error || !data) {
      console.error('Address validation failed', response);
      this.lastValidClaimAddress = undefined;
      this.addressValidationResponse.set(response);
      this.addressValidationError.set('Address validation is unavailable. Please try again.');
      return;
    }

    if (this.isAcceptedAddress(data)) {
      if (data.candidates?.length >= 1) {
        this.applyAddressCandidate(data.candidates[0], data.status);
      }
      this.clearAddressValidationResult();
      this.currentPage.set('warranty');
      return;
    }

    this.lastValidClaimAddress = undefined;
    this.addressValidationResponse.set(response);
  }

  private isAcceptedAddress(data: AddressValidationData): boolean {
    return data?.status === AddressValidationStatus.VALID && !data?.errors?.length;
  }

  private isLastValidClaimAddress(address: Address): boolean {
    return !!this.lastValidClaimAddress &&
      (['line1', 'line2', 'city', 'state', 'zipCode', 'country'] satisfies (keyof Address)[]).every(field => {
        return normalizeAddressValue(address[field]) === normalizeAddressValue(this.lastValidClaimAddress[field])
      });
  }

  private resetAddressControlState(): void {
    const controls = this.warrantyForm.controls;
    [
      controls.line1,
      controls.line2,
      controls.city,
      controls.state,
      controls.zipCode,
      controls.country,
    ].forEach(control => {
      control.markAsPristine();
      control.markAsUntouched();
      control.updateValueAndValidity();
    });
  }

  private clearAddressValidationResult(): void {
    this.addressValidationResponse.set(undefined);
    this.addressValidationError.set(undefined);
  }

  private loadFile(file: File, imageType: WarrantyImageType): void {
    if (!WarrantyImageTypes.includes(imageType)) {
      return;
    }
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      this.applicationService.addMessage('File type not supported, please use JPG/JPEG or PNG', MessageType.WARNING);
      return;
    }
    if (file.size <= MAX_IMAGE_UPLOAD_BYTES) {
      this.readFile(file, imageType);
      return;
    }

    this.applicationService.bump(1);
    from(compressImage(file)).pipe(
      finalize(() => { this.applicationService.bump(-1); }),
    ).subscribe({
      next: image => {
        if (image.size > MAX_IMAGE_UPLOAD_BYTES) {
          this.applicationService.addMessage(imageTooLargeError, MessageType.WARNING);
          return;
        }
        this.readFile(image, imageType);
      },
      error: (error) => {
        console.error('Image compression failed', error);
        this.applicationService.addMessage('Error reading the File', MessageType.ERROR);
      },
    });
  }

  private readFile(file: File, imageType: WarrantyImageType): void {
    const targetImageType = resolveWarrantyImageType(imageType, this.warrantyForm.getRawValue());
    const fileReader = new FileReader();
    fileReader.onload = (e: ProgressEvent<FileReader>) => {
      const value = e.target?.result as string;
      this.warrantyForm.controls[targetImageType].setValue(value);
      if (this.imageValidationError()) {
        this.imageValidationError.set(this.getImageValidationError());
      }
      this.imageLoaded.emit({ imageType: targetImageType, file, value });
    };
    fileReader.onerror = () => {
      this.applicationService.addMessage('Error reading the File', MessageType.ERROR);
    };
    fileReader.readAsDataURL(file);
  }

  private getImageValidationError(): string | undefined {
    const hasReceipt: boolean = !!this.warrantyForm.controls.receipt?.value;
    const hasDamage: boolean = WarrantyDamageImageTypes.some(x => !!this.warrantyForm.controls[x]?.value);

    if (!hasReceipt) {
      return missingReceiptImageError;
    }
    else if (this.isImageRequired('damage1') && !hasDamage) {
      return missingDamageImageError;
    }

    return undefined;
  }

  private shouldConfirmMissingSerial(): boolean {
    return this.shouldShowSerialFields() &&
      !String(this.warrantyForm.controls.batIdentificationCode.value || '').trim() &&
      !String(this.warrantyForm.controls.serial?.value || '').trim();
  }

  private openSerialConfirmationDialog(): void {
    this.dialog.open(SerialConfirmationDialogComponent, {
      disableClose: true,
      maxWidth: 'calc(100vw - 2em)',
      width: '30em',
    }).afterClosed().pipe(take(1)).subscribe(confirmed => {
      if (confirmed) {
        this.submitWarranty.emit();
      }
    });
  }

}
